import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { datadogRum } from '@datadog/browser-rum';
import { FeatureFlags } from '@enums/feature-flags.enum';
import { FeatureFlagService } from '@services/feature-flag.service';
import { take } from 'rxjs/operators';

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/,
  /Loading chunk .* failed/,
];

export const SESSION_KEY_DESTINATION = 'chunk_recovery_destination';
export const SESSION_KEY_COUNT = 'chunk_recovery_count';

@Injectable()
export class ChunkErrorHandlerService extends ErrorHandler {
  private flagValue: boolean | null = null;
  private flagResolved = false;

  constructor(private injector: Injector) {
    super();
    this.subscribeToFlag();
  }

  override handleError(error: unknown): void {
    if (!this.isChunkLoadError(error)) {
      super.handleError(error);
      return;
    }

    const failedUrl = this.getIntendedDestination();
    const willReload = this.shouldReload();

    datadogRum?.addAction('chunk_load_recovery', {
      failedUrl: this.stripQueryString(failedUrl),
      willReload,
      flagResolved: this.flagResolved,
      flagValue: this.flagValue,
    });

    if (!willReload) {
      super.handleError(error);
      return;
    }

    const recoveryCount = this.getRecoveryCount();
    if (recoveryCount >= 1) {
      datadogRum?.addAction('chunk_load_recovery_loop_prevented', {
        failedUrl: this.stripQueryString(failedUrl),
        recoveryCount,
      });
      super.handleError(error);
      return;
    }

    try {
      sessionStorage.setItem(SESSION_KEY_COUNT, String(recoveryCount + 1));
      if (failedUrl) {
        sessionStorage.setItem(SESSION_KEY_DESTINATION, failedUrl);
      }
      location.reload();
    } catch {
      super.handleError(error);
    }
  }

  private isChunkLoadError(error: unknown): boolean {
    const message = this.extractErrorMessage(error);
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
  }

  private extractErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }
    if (
      typeof error === 'object' &&
      error !== null &&
      'rejection' in error &&
      (error as { rejection: unknown }).rejection instanceof Error
    ) {
      return (error as { rejection: Error }).rejection.message;
    }
    return String(error ?? '');
  }

  private shouldReload(): boolean {
    if (!this.flagResolved) {
      return false;
    }
    return this.flagValue === true;
  }

  private getIntendedDestination(): string | null {
    try {
      const router = this.injector.get(Router);
      const nav = router.getCurrentNavigation();
      if (nav) {
        return nav.extractedUrl.toString();
      }
    } catch {
      // Router may not be available yet
    }
    // Hash-based routing: actual route is in location.hash
    return location.hash ? location.hash.slice(1) : '/';
  }

  private getRecoveryCount(): number {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY_COUNT);
      return raw ? parseInt(raw, 10) || 0 : 0;
    } catch {
      return 0;
    }
  }

  private stripQueryString(url: string | null): string | null {
    if (!url) {
      return url;
    }
    const qIndex = url.indexOf('?');
    return qIndex >= 0 ? url.substring(0, qIndex) : url;
  }

  private subscribeToFlag(): void {
    try {
      const featureFlagService = this.injector.get(FeatureFlagService);
      featureFlagService
        .getFlag(FeatureFlags.LP_4114_STALE_CHUNK_FIX, false)
        .pipe(take(1))
        .subscribe({
          next: (value) => {
            this.flagValue = value === true;
            this.flagResolved = true;
          },
          error: () => {
            this.flagValue = false;
            this.flagResolved = true;
          },
        });
    } catch {
      this.flagValue = false;
      this.flagResolved = true;
    }
  }
}
